import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Link as ExternalLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { green, grey, purple, red } from '@material-ui/core/colors';
import { Link } from 'react-router-dom';

import { getApolloContact } from '@api/apollo-enrichment-contact';
import { ApolloContactDetail, ApolloDbMatch } from '../apollo-enriched-contacts.types';
import { DbExistsMark, EmailCopy, EmailStatusChips } from '../columns/status';

interface ContactDetailsModalProps {
  contactId: number | null;
  open: boolean;
  onClose: () => void;
}

const personSearch = (filters: { id: string; op: string; value: string }[]) => {
  const query = filters
    .map(
      (filter, index) =>
        `filters[${index}].id=${filter.id}&filters[${index}].value.${filter.op}=${encodeURIComponent(filter.value)}`
    )
    .join('&');
  return `/person-management/search?${query}`;
};

const deletedValue = (status?: string | null) =>
  (status || '').trim().toLowerCase() === 'active' ? 'false' : 'true';

const linkedinQuery = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

const statusLabel = (status?: string | null) =>
  (status || '').trim().toLowerCase() === 'active' ? 'Active' : 'Inactive';

const isActiveStatus = (status?: string | null) =>
  (status || '').trim().toLowerCase() === 'active';

const uniqueEntityCount = (matches: ApolloDbMatch[]) => {
  const ids = new Set(
    matches.map(match => (match.vtr_id || '').trim()).filter(Boolean)
  );

  return ids.size || matches.length;
};

const matchesFor = (contact: ApolloContactDetail, attribute: string) =>
  (contact.db_exists_on || []).filter(
    match => (match.attribute || '').trim().toLowerCase() === attribute
  );

const DbMatchLinks = ({
  matches,
  searchFor,
}: {
  matches: ApolloDbMatch[];
  searchFor: (match: ApolloDbMatch) => string;
}) => {
  if (!matches.length) {
    return null;
  }

  return (
    <Box mt={0.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
      {matches.map((match, index) => (
        <Link
          key={`${match.vtr_id || index}-${match.status}`}
          to={searchFor(match)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: (match.status || '').toLowerCase() === 'active' ? green[700] : red[700],
            fontWeight: 600,
            fontSize: 13,
          }}>
          In DB · {statusLabel(match.status)}
        </Link>
      ))}
    </Box>
  );
};

const LinkedInDbMatches = ({
  matches,
  linkedinUrl,
}: {
  matches: ApolloDbMatch[];
  linkedinUrl: string;
}) => {
  if (!matches.length || !linkedinUrl) {
    return null;
  }

  const activeMatches = matches.filter(match => isActiveStatus(match.status));
  const inactiveMatches = matches.filter(match => !isActiveStatus(match.status));
  const activeCount = uniqueEntityCount(activeMatches);
  const inactiveCount = uniqueEntityCount(inactiveMatches);
  const totalCount = uniqueEntityCount(matches);
  const hasBothStatuses = activeCount > 0 && inactiveCount > 0;

  const filters = [
    {
      id: 'linkedin_url',
      op: '$contains',
      value: linkedinQuery(linkedinUrl),
    },
  ];

  if (!hasBothStatuses) {
    filters.push({
      id: 'is_deleted',
      op: '$eq',
      value: activeCount > 0 ? 'false' : 'true',
    });
  }

  const withCount = (label: string, count: number) =>
    count > 1 ? `${label} (${count})` : label;

  const label = hasBothStatuses
    ? `In DB · ${totalCount} (${withCount('Active', activeCount)}, ${withCount(
        'Inactive',
        inactiveCount
      )})`
    : `In DB · ${withCount(activeCount > 0 ? 'Active' : 'Inactive', totalCount)}`;

  return (
    <Box mt={0.5}>
      <Link
        to={personSearch(filters)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: activeCount > 0 ? green[700] : red[700],
          fontWeight: 600,
          fontSize: 13,
        }}>
        {label}
      </Link>
    </Box>
  );
};

const DetailItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box
    p={1.5}
    height="100%"
    border={1}
    borderColor={grey[200]}
    borderRadius={8}
    bgcolor="#fff">
    <Typography
      variant="caption"
      color="textSecondary"
      style={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box mt={0.5}>{children}</Box>
  </Box>
);

const TextValue = ({ value }: { value?: string | null }) => (
  <Typography variant="body1" style={{ fontWeight: 500 }}>
    {value || '-'}
  </Typography>
);

const ContactDetailsModal: React.FC<ContactDetailsModalProps> = ({
  contactId,
  open,
  onClose,
}) => {
  const [contact, setContact] = useState<ApolloContactDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || contactId == null) {
      return;
    }

    setLoading(true);
    setError('');
    setContact(null);

    const subscription = getApolloContact(contactId).subscribe({
      next: response => {
        setContact(response.data || null);
        setLoading(false);
      },
      error: () => {
        setError('Unable to load contact details.');
        setLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [open, contactId]);

  const emailMatches = contact ? matchesFor(contact, 'email') : [];
  const linkedinMatches = contact ? matchesFor(contact, 'linkedin') : [];
  const hasDbMatch =
    Boolean(contact?.db_exists) || emailMatches.length > 0 || linkedinMatches.length > 0;
  const nameSearch = contact?.person_name
    ? personSearch([{ id: 'name', op: '$contains', value: contact.person_name }])
    : '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box px={3} py={2.5} style={{ background: purple[700], color: '#fff' }}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          {contact?.person_name || 'Contact Details'}
        </Typography>
        <Typography variant="body2" style={{ opacity: 0.9, marginTop: 4 }}>
          {[contact?.designation, contact?.company].filter(Boolean).join(' · ') ||
            'Apollo enriched contact'}
        </Typography>
      </Box>

      <DialogContent style={{ background: grey[50], padding: 24 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}

        {contact ? (
          <>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
              Contact Information
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
              <Box flex="1 1 280px">
                <DetailItem label="Person Name">
                  {!hasDbMatch && nameSearch ? (
                    <Link to={nameSearch} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                      {contact.person_name}
                    </Link>
                  ) : (
                    <TextValue value={contact.person_name} />
                  )}
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Designation">
                  <TextValue value={contact.designation} />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Company">
                  <TextValue value={contact.company} />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Email">
                  {contact.email ? (
                    <EmailCopy value={contact.email} />
                  ) : (
                    <TextValue value="-" />
                  )}
                  <DbMatchLinks
                    matches={emailMatches}
                    searchFor={match =>
                      personSearch([
                        { id: 'email_id', op: '$contains', value: contact.email || '' },
                        { id: 'is_deleted', op: '$eq', value: deletedValue(match.status) },
                      ])
                    }
                  />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Email Status">
                  <EmailStatusChips status={contact.email_status} />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="DB Exists">
                  {!hasDbMatch && nameSearch ? (
                    <Link to={nameSearch} target="_blank" rel="noopener noreferrer">
                      <DbExistsMark dbExists={false} />
                    </Link>
                  ) : (
                    <DbExistsMark dbExists={hasDbMatch} />
                  )}
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Phone">
                  <TextValue value={contact.phone} />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="Location">
                  <TextValue value={contact.location} />
                </DetailItem>
              </Box>
              <Box flex="1 1 280px">
                <DetailItem label="LinkedIn">
                  {contact.linkedin_url ? (
                    <ExternalLink
                      href={contact.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer">
                      View LinkedIn Profile
                    </ExternalLink>
                  ) : (
                    <TextValue value="-" />
                  )}
                  <LinkedInDbMatches
                    matches={linkedinMatches}
                    linkedinUrl={contact.linkedin_url || ''}
                  />
                </DetailItem>
              </Box>
            </Box>

            <Typography variant="subtitle1" style={{ fontWeight: 600, margin: '28px 0 12px' }}>
              Request Information
            </Typography>
            <Box bgcolor="#fff" border={1} borderColor={grey[200]} borderRadius={8} overflow="hidden">
              {(contact.requests || []).length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: purple[50] }}>
                      <TableCell style={{ fontWeight: 600, width: 56 }}>#</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Requested By</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Request ID</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>User Query</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contact.requests.map((request, index) => (
                      <TableRow key={request.request_id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{request.requested_by || '-'}</TableCell>
                        <TableCell>{request.request_id || '-'}</TableCell>
                        <TableCell>{request.user_query || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Box p={2}>
                  <Typography variant="body2" color="textSecondary">
                    No requests
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : null}
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px 16px' }}>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDetailsModal;
